import React from 'react'
import PropTypes from 'prop-types'
import { CloudX } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Badge } from '@/components/ui/badge'
import { Divider } from '@/components/ui/divider'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { formatNumber } from '@/utils/number'

import Icon from '../../Icons'

const BuildingData = ({ buildingData, loading }) => {
  const { t } = useTranslation(['building-analytics'])
  const selectedBuilding = useSelector(selectActiveBuilding)
  const showAppUserStatus = Boolean(selectedBuilding?.appUserStatusAvailable)

  return (
    <div className="building-data">
      <div className="building-data__header">
        <div className="title-with-icon">
          <Icon
            styles={{ width: '24px', height: '24px' }}
            viewBox="0 0 24 24"
            icon="buildingData"
          />
          <h3>{t('building-analytics:buildingData.title')}</h3>
        </div>
      </div>
      {loading ? (
        <div className="loading">
          <h3>....</h3>
          <h3>{t('building-analytics:common.loading')}</h3>
        </div>
      ) : (
        <div className="building-data__main-block">
          {!!buildingData && !!buildingData.building && buildingData.building.residents ? (
            <React.Fragment>
              {showAppUserStatus ? (
                <h4 className="building-data__main-block__title">
                  {t('building-analytics:buildingData.totalResidents')}
                </h4>
              ) : (
                <h4 className="building-data__main-block__title">
                  {t('building-analytics:buildingData.residentsUsingApp')}
                </h4>
              )}
              {showAppUserStatus ? (
                <h3 className="building-data-reviews__top-wrapper__number">
                  {formatNumber(buildingData.building.residents)}
                </h3>
              ) : (
                <div className="building-data-reviews__top-wrapper">
                  <h3 className="building-data-reviews__top-wrapper__number">
                    {formatNumber(buildingData.building.residents)}
                  </h3>
                  {buildingData.building.residents_increase ? (
                    <div
                      className={`building-data-reviews__top-wrapper__percent-wrapper ${
                        buildingData.building.residents_increase > 0 ? 'color-green' : 'color-red'
                      }`}
                    >
                      <span className="percent-text">
                        {buildingData.building.residents_increase > 0
                          ? '▲ +'
                          : buildingData.building.residents_increase < 0
                            ? '▼ -'
                            : ''}
                        {buildingData.building.residents_increase}
                      </span>
                    </div>
                  ) : null}
                </div>
              )}
              {buildingData.building.hasIntegration ? (
                <div className="building-data__pms-inline">
                  <span>
                    {t('building-analytics:buildingData.future')}{' '}
                    <strong>{formatNumber(buildingData.building.residents_future)}</strong>
                  </span>
                  <span>
                    {t('building-analytics:buildingData.current')}{' '}
                    <strong>{formatNumber(buildingData.building.residents_current)}</strong>
                  </span>
                  <span>
                    {t('building-analytics:buildingData.notice')}{' '}
                    <strong>{formatNumber(buildingData.building.residents_notice)}</strong>
                  </span>
                </div>
              ) : null}
              {showAppUserStatus && (
                <React.Fragment>
                  <Divider className="building-data__divider" />
                  <h4 className="building-data__main-block__title">
                    {t('building-analytics:buildingData.appUsers')}
                  </h4>
                  <div className="building-data__app-users">
                    <span className="building-data__app-users__count-group">
                      <span className="building-data__app-users__count">
                        {formatNumber(buildingData.building.appUsers)}
                      </span>
                      <span className="building-data__app-users__total">
                        / {formatNumber(buildingData.building.residents)}
                      </span>
                    </span>
                    <Badge color="neutral" size="small" variant="light">
                      {buildingData.building.residents
                        ? Math.round(
                            ((buildingData.building.appUsers ?? 0) /
                              buildingData.building.residents) *
                              100,
                          )
                        : 0}
                      %
                    </Badge>
                  </div>
                </React.Fragment>
              )}
            </React.Fragment>
          ) : (
            <div className="no-data">
              <CloudX size={24} />
              <h3>{t('building-analytics:common.noDataTitle')}</h3>
              <p>{t('building-analytics:common.noDataDescription')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

BuildingData.propTypes = {
  buildingData: PropTypes.object,
  loading: PropTypes.bool,
}

export default BuildingData
